/* session-banner.js — the visible half of the session guard.
 *
 * Whenever the browser is NOT a plain shopper session, this pins a SMALL pill
 * to the bottom-left corner saying what mode you are in. Clicking it opens the
 * detail: what has been blocked, and a one-click EXIT.
 *
 * WHY IT MUST EXIST (contract rule: "loud and unmissable", + the residue exit):
 *   • Before this, the only signal was a one-shot toast you could scroll past,
 *     and there was NO exit anywhere — the adopted identity simply persisted
 *     into the operator's own browsing forever.
 *   • Exit runs SE_Session.end(), which un-stamps the cart FIRST, restores the
 *     operator's own identity from the snapshot taken at begin(), clears the
 *     session's storage, and reloads.
 *
 * SMALL BY REQUEST (Dorian 2026-08-26): it used to be a full-width bar pinned
 * across the bottom, and it pushed the page up by its own height to keep the
 * cart bar clear. That is a lot of screen for a mode indicator you only need to
 * glance at. Now: a corner pill that costs no layout at all, expanding to the
 * full message + exit only when clicked. "Unmissable" is preserved by the
 * colour, the permanent placement and the pulse — not by size.
 *
 * Renders NOTHING for a real shopper — zero DOM, zero cost. Self-contained per
 * the isolation principle: reads state only via window.SE_Session.
 */
(function () {
  'use strict';

  if (window.__AGB_sessionBanner) return;
  window.__AGB_sessionBanner = true;

  function boot() {
    var S = window.SE_Session;
    if (!S || S.kind() === 'shopper') return;      // real shopper -> nothing at all

    // PREVIEW THEMES ONLY, deliberately. Preview is where the test personas
    // run, and a staff member browsing the LIVE store has no use for a bar.
    if (!S.isPreview()) return;

    var kind = S.kind();
    var isAssumed = kind === 'assumed';
    // THE TEAM / TEST PILL IS RETIRED (Dorian 2026-09-08: "this you can
    // remove"). It existed to say "this visit is marked internal" and to open
    // the previewer tools - the "Media on this page" list that opened records
    // in Agilite Media - and the phone review bar (agmf-preview-admin.js) has
    // replaced that. The marking itself is session-guard's and is untouched;
    // the preview build badge stands on its own again (nothing absorbs it).
    // The pill for an ASSUMED identity stays: "Exit and restore me" is the
    // only way out of impersonation.
    if (!isAssumed) return;
    var accent = isAssumed ? '#8f1814' : '#3f4a2e';

    // ── the pill (collapsed state) ────────────────────────────────────────
    // bottom-LEFT on purpose: chat widgets and back-to-top buttons live in the
    // bottom-right corner on this theme.
    var wrap = document.createElement('div');
    wrap.setAttribute('role', 'status');
    wrap.style.cssText = [
      'position:fixed', 'left:12px', 'bottom:12px', 'z-index:2147483600',
      'font:11px/1.35 system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
      'display:flex', 'flex-direction:column', 'align-items:flex-start', 'gap:6px',
      'max-width:min(300px,calc(100vw - 24px))'
    ].join(';');

    var pill = document.createElement('button');
    pill.type = 'button';
    pill.setAttribute('aria-expanded', 'false');
    pill.style.cssText = [
      'display:inline-flex', 'align-items:center', 'gap:7px',
      'cursor:pointer', 'font:inherit', 'font-weight:700',
      'letter-spacing:.06em', 'text-transform:uppercase',
      'padding:5px 11px', 'border-radius:999px', 'border:0',
      'color:#fff', 'background:' + accent,
      'box-shadow:0 2px 10px rgba(0,0,0,.3)', 'opacity:.92'
    ].join(';');

    var dot = document.createElement('span');
    dot.setAttribute('aria-hidden', 'true');
    dot.style.cssText = [
      'width:6px', 'height:6px', 'border-radius:50%', 'flex:0 0 auto',
      'background:#fff', 'opacity:.9'
    ].join(';');

    var pillText = document.createElement('span');
    pillText.textContent = isAssumed ? 'Viewing as someone else' : 'Team / test';

    // the blocked-action counter rides IN the pill, so the one number worth
    // seeing at a glance needs no click
    var count = document.createElement('span');
    count.style.cssText = [
      'font-variant-numeric:tabular-nums', 'font-weight:700',
      'padding:1px 6px', 'border-radius:999px',
      'background:rgba(255,255,255,.22)', 'display:none'
    ].join(';');

    pill.appendChild(dot);
    pill.appendChild(pillText);
    pill.appendChild(count);

    // ── Absorb the preview-build badge (one pill, not two) ────────────────
    // preview-build-marker.liquid publishes { at, text, shown } on
    // window.__AGB_PREVIEW_BUILD and fires agb:preview-build-ready whenever it
    // changes. While THIS pill is on screen it displays the build facet and
    // the marker stays hidden — two corner pills both meaning "not the real
    // store" is one too many (Dorian, 2026-08-27). Setting `absorbed` is what
    // tells the marker to stay hidden, whichever file happened to load first.
    var build = document.createElement('span');
    build.style.cssText = [
      'font:10px/1.3 ui-monospace,Menlo,Consolas,monospace',
      'font-weight:400', 'text-transform:none', 'letter-spacing:0',
      'opacity:.85', 'display:none', 'white-space:nowrap',
      'border-left:1px solid rgba(255,255,255,.35)', 'padding-left:7px'
    ].join(';');
    pill.appendChild(build);

    function absorbBuild() {
      var pb = (window.__AGB_PREVIEW_BUILD = window.__AGB_PREVIEW_BUILD || {});
      pb.absorbed = true;                       // the marker checks this
      var el = document.getElementById('preview-build-marker');
      if (el) el.style.display = 'none';
      if (pb.shown && pb.text) {
        build.textContent = pb.text;
        build.title = 'preview build — ' + (pb.at || '');
        build.style.display = 'inline';
      } else {
        // Panel disabled the badge -> no build facet here either.
        build.style.display = 'none';
      }
    }
    absorbBuild();
    document.addEventListener('agb:preview-build-ready', absorbBuild);

    // ── the detail (expanded state) ───────────────────────────────────────
    var panel = document.createElement('div');
    panel.hidden = true;
    panel.style.cssText = [
      'order:-1',                                  // opens UPWARD, above the pill
      'padding:10px 12px', 'border-radius:8px',
      'font-size:12px', 'line-height:1.45',
      'color:#fff', 'background:' + accent,
      'box-shadow:0 4px 16px rgba(0,0,0,.35)'
    ].join(';');

    var msg = document.createElement('div');
    msg.textContent = isAssumed
      ? 'Nothing you do here is saved to their account or to our records.'
      : 'This visit is marked internal, so it stays out of the shopper reports.';
    panel.appendChild(msg);

    var detail = document.createElement('div');
    detail.style.cssText = 'margin-top:4px;opacity:.85;font-size:11px';
    panel.appendChild(detail);

    // ── Previewer tools ───────────────────────────────────────────────────
    // The pill is THE contextual element for a previewer: whatever a
    // preview-only snippet offers (preview-tools.liquid registers sections on
    // window.__AGB_PREVIEW_TOOLS) is rendered here, on open, and never while
    // the panel is closed. Neither file imports the other.
    var tools = document.createElement('div');
    tools.hidden = true;
    tools.style.cssText = 'margin-top:8px;padding-top:8px;border-top:1px solid rgba(255,255,255,.25)';
    panel.appendChild(tools);
    function renderTools() {
      var reg = window.__AGB_PREVIEW_TOOLS;
      var list = (reg && reg.sections) || [];
      tools.replaceChildren();
      tools.hidden = !list.length;
      for (var i = 0; i < list.length; i++) {
        var c = document.createElement('div');
        try { list[i].render(c); } catch (e) {}
        tools.appendChild(c);
      }
    }
    document.addEventListener('agb:preview-tools-ready', function () {
      if (!panel.hidden) renderTools();
    });

    function refresh() {
      var n = 0;
      try { n = S.blocked().length; } catch (e) {}
      count.textContent = n ? String(n) : '';
      count.style.display = n ? 'inline-block' : 'none';
      detail.textContent = n
        ? (n + (n === 1 ? ' action kept private' : ' actions kept private'))
        : '';
    }
    refresh();
    setInterval(refresh, 2000);

    // The exit button appears ONLY when there is a snapshot to restore from.
    //
    // A session that was merely RECOGNISED (a staff00- browser that has always
    // been itself) never went through begin(), so end() cannot put anything
    // back — it can only delete the identity and the 365-day cookie, i.e. strip
    // the staff flag that keeps that person's browsing out of the shopper
    // reports. They would have to re-flag from their team link, and it happens
    // in THEIR browser storage, so it cannot be undone from our side.
    // A control that can only destroy is worse than no control, so: no button.
    var canRestore = false;
    try { canRestore = !!(S.canRestore && S.canRestore()); } catch (e) {}

    if (canRestore) {
      var exit = document.createElement('button');
      exit.type = 'button';
      exit.textContent = 'Exit and restore me';
      exit.style.cssText = [
        'margin-top:8px', 'width:100%', 'cursor:pointer', 'font:inherit',
        'font-weight:700', 'padding:6px 12px', 'border-radius:4px',
        'border:1px solid rgba(255,255,255,.5)',
        'background:rgba(255,255,255,.14)', 'color:#fff'
      ].join(';');
      exit.addEventListener('click', function (e) {
        e.stopPropagation();
        exit.disabled = true;
        exit.textContent = 'Restoring…';
        try { S.end(); } catch (err) { location.reload(); }
      });
      panel.appendChild(exit);
    }

    pill.addEventListener('click', function () {
      var open = panel.hidden;
      panel.hidden = !open;
      if (open) renderTools();
      pill.setAttribute('aria-expanded', open ? 'true' : 'false');
      pill.style.opacity = open ? '1' : '.92';
    });

    wrap.appendChild(panel);
    wrap.appendChild(pill);
    (document.body || document.documentElement).appendChild(wrap);
    // No body padding: a corner pill costs the page no layout, which is the
    // point of shrinking it.
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
